import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, photoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star, LogIn, LogOut, Loader2, Plus, Copy, Share2, Bell, BellOff, Link2 } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, isPushEnabled, enablePush, disablePush, pushPermission } from "@/lib/pushNotifications";
import { FanionBadge, FANION_LABELS } from "@/components/FanionBadge";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [yoles, setYoles] = useState([]);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [savingFav, setSavingFav] = useState(false);
  const [selectedFav, setSelectedFav] = useState(user?.favorite_yole_id || "");
  const [newLeagueName, setNewLeagueName] = useState("");
  const [newLeagueCode, setNewLeagueCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [myLeague, setMyLeague] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [thresholds, setThresholds] = useState(null);
  const supportsPush = pushSupported();
  const permission = supportsPush ? pushPermission() : "denied";

  useEffect(() => {
    setPushOn(isPushEnabled());
    api.get("/fanion/thresholds").then(({ data }) => setThresholds(data)).catch(() => {});
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        disablePush();
        setPushOn(false);
        toast.success("Notifications navigateur désactivées");
      } else {
        const res = await enablePush();
        if (res.ok) {
          setPushOn(true);
          toast.success("Notifications navigateur activées !");
          // Fire a demo notification
          try {
            const reg = await navigator.serviceWorker.getRegistration();
            const opts = { body: "Tu recevras une alerte quand une étape est réglée ou que tu gagnes des points.", tag: "yoles-welcome-push", icon: "/favicon.ico", badge: "/favicon.ico" };
            if (reg && reg.showNotification) reg.showNotification("Ma Petite Yole", opts);
            else new Notification("Ma Petite Yole", opts);
          } catch (err) { console.warn("Demo notification failed:", err); }
        } else {
          toast.error(res.error || "Impossible d'activer les notifications");
        }
      }
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/yoles");
        setYoles(data);
      } catch (err) { console.warn("Failed to load yoles:", err); }
    })();
  }, []);

  useEffect(() => {
    if (!user?.league_id) {
      setMyLeague(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/league/me");
        setMyLeague(data);
      } catch (err) { console.warn("Failed to load league:", err); }
    })();
  }, [user?.league_id]);

  useEffect(() => {
    setSelectedFav(user?.favorite_yole_id || "");
  }, [user]);

  const joinLeague = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setJoining(true);
    try {
      await api.post("/league/join", { code });
      toast.success("Ligue rejointe !");
      setCode("");
      await refreshUser();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setJoining(false); }
  };

  const leaveLeague = async () => {
    if (!confirm("Quitter cette ligue ?")) return;
    try {
      await api.post("/league/leave");
      toast.success("Tu as quitté la ligue");
      await refreshUser();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createLeague = async (e) => {
    e.preventDefault();
    if (!newLeagueName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/league/create", {
        name: newLeagueName.trim(),
        code: newLeagueCode.trim() || undefined,
      });
      setLastCreated(data);
      setNewLeagueName("");
      setNewLeagueCode("");
      toast.success(`Ligue "${data.name}" créée !`);
      await refreshUser();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const copyCode = (c) => {
    navigator.clipboard.writeText(c);
    toast.success(`Code copié : ${c}`);
  };

  const copyInviteLink = (c) => {
    const link = `${window.location.origin}/join/${c}`;
    navigator.clipboard.writeText(link);
    toast.success("Lien d'invitation copié !");
  };

  const saveFavorite = async (yoleId) => {
    setSavingFav(true);
    setSelectedFav(yoleId);
    try {
      await api.post("/user/favorite-yole", { yole_id: yoleId || null });
      toast.success(yoleId ? "Yole favorite mise à jour" : "Yole favorite retirée");
      await refreshUser();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingFav(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[color:var(--deep)]">
          Mon profil
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Rejoins une ligue et choisis ta yole favorite (+5 pts si elle gagne une étape).
        </p>
      </div>

      {/* Wallet */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Total points</div>
        <div className="font-mono text-4xl font-extrabold text-[color:var(--deep)] mt-1">
          {user?.wallet_points || 0}<span className="text-sm opacity-60 ml-2">pts</span>
        </div>
      </div>

      {/* Fanion — grand affichage */}
      <div
        className="relative overflow-hidden bg-[color:var(--deep)] text-white rounded-2xl p-5"
        data-testid="fanion-card"
      >
        <div className="absolute -right-6 -top-6 opacity-15 pointer-events-none">
          <FanionBadge level={user?.fanion_level || 1} size="xl" />
        </div>
        <div className="relative flex items-start gap-5">
          <FanionBadge level={user?.fanion_level || 1} size="lg" animated />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--sunshine)] font-bold">
              Ton fanion — Niveau {user?.fanion_level || 1}
            </div>
            <div className="brand-wordmark text-3xl mt-1" data-testid="fanion-label">
              {user?.fanion_label || "Mousse"}
            </div>
            <div className="text-xs opacity-80 mt-2 font-mono">
              {user?.fanion_stats?.points ?? 0} pts · {user?.fanion_stats?.stages_played ?? 0} étape{(user?.fanion_stats?.stages_played ?? 0) > 1 ? "s" : ""} · {user?.fanion_stats?.podiums ?? 0} podium{(user?.fanion_stats?.podiums ?? 0) > 1 ? "s" : ""}
            </div>
          </div>
        </div>

        {/* Next-milestone progress */}
        {thresholds && (user?.fanion_level || 1) < 4 && (() => {
          const nextLvl = (user?.fanion_level || 1) + 1;
          const th = thresholds.levels[nextLvl];
          const s = user?.fanion_stats || { points: 0, stages_played: 0, podiums: 0 };
          const need = (want, have) => Math.max(0, want - have);
          const missing = [];
          if (need(th.points, s.points)) missing.push(`+${need(th.points, s.points)} pts`);
          if (need(th.stages, s.stages_played)) missing.push(`+${need(th.stages, s.stages_played)} étape${need(th.stages, s.stages_played) > 1 ? "s" : ""}`);
          if (need(th.podiums, s.podiums)) missing.push(`+${need(th.podiums, s.podiums)} podium${need(th.podiums, s.podiums) > 1 ? "s" : ""}`);
          return (
            <div className="relative mt-4 pt-4 border-t border-white/10" data-testid="next-milestone">
              <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">Prochain palier</div>
              <div className="mt-1 flex items-center gap-2">
                <FanionBadge level={nextLvl} size="sm" />
                <span className="brand-wordmark text-lg text-[color:var(--sunshine)]">{th.label}</span>
              </div>
              <div className="text-xs opacity-80 mt-1">
                {missing.length ? `Il te manque ${missing.join(" · ")}` : "Palier atteint — attends le prochain règlement d'étape."}
              </div>
            </div>
          );
        })()}

        <div className="relative mt-4 pt-4 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold mb-2">Progression</div>
          <div className="grid grid-cols-4 gap-2">
            {[1, 2, 3, 4].map((lvl) => {
              const active = (user?.fanion_level || 1) >= lvl;
              return (
                <div
                  key={lvl}
                  className={`flex flex-col items-center gap-1 py-2 rounded-xl border ${active ? "border-[color:var(--sunshine)] bg-white/5" : "border-white/10 opacity-45"}`}
                  data-testid={`fanion-step-${lvl}`}
                >
                  <FanionBadge level={lvl} size="md" />
                  <div className="text-[9px] font-bold uppercase tracking-widest text-center leading-tight">
                    {FANION_LABELS[lvl]}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] opacity-70 mt-3 leading-relaxed">
            {thresholds ? (
              <>
                <strong>Bwa Dréssé</strong> : {thresholds.levels[2].hint} ·
                <strong className="ml-1">Patron</strong> : {thresholds.levels[3].hint} ·
                <strong className="ml-1">MAPIPI</strong> : {thresholds.levels[4].hint}
              </>
            ) : (
              <>Calcule tes points en pronostiquant chaque étape · max ~134 pts / étape.</>
            )}
          </div>
        </div>
      </div>

      {/* Push notifications */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="push-notifications-card">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
              {pushOn ? <Bell className="h-3.5 w-3.5 text-[color:var(--coral)]" /> : <BellOff className="h-3.5 w-3.5" />}
              Alertes navigateur
            </div>
            <div className="text-sm text-slate-600">
              {!supportsPush
                ? "Ton navigateur ne supporte pas les notifications."
                : permission === "denied"
                ? "Les notifications sont bloquées. Autorise-les dans les réglages du navigateur pour ce site."
                : pushOn
                ? "Tu reçois une alerte OS quand une étape est réglée ou que tu gagnes des points."
                : "Active-les pour être prévenu dès qu'une étape est réglée."}
            </div>
          </div>
          <Button
            onClick={togglePush}
            disabled={pushBusy || !supportsPush || permission === "denied"}
            data-testid="toggle-push"
            className={`rounded-full h-11 px-4 font-bold flex-shrink-0 ${
              pushOn
                ? "bg-slate-200 hover:bg-slate-300 text-slate-700"
                : "bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
            }`}
          >
            {pushBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : pushOn ? "Désactiver" : "Activer"}
          </Button>
        </div>
      </div>

      {/* League */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Ligue</div>
        {user?.league_id && !user?.league_is_default ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display font-extrabold text-lg text-[color:var(--deep)] truncate">{user.league_name}</div>
                <div className="text-xs text-slate-500">
                  {myLeague ? `${myLeague.member_count} membre${myLeague.member_count > 1 ? "s" : ""}` : "…"}
                </div>
              </div>
              <Button
                onClick={leaveLeague}
                variant="outline"
                data-testid="leave-league-btn"
                className="rounded-full text-xs font-bold text-red-500 border-red-200 hover:bg-red-50 flex-shrink-0"
              >
                <LogOut className="h-3.5 w-3.5 mr-1" /> Quitter
              </Button>
            </div>

            {myLeague?.code && (
              <div className="rounded-2xl bg-gradient-to-br from-[color:var(--sunshine)]/25 to-[color:var(--coral)]/10 border-2 border-[color:var(--sunshine)] p-4" data-testid="league-invite-card">
                <div className="text-[10px] uppercase font-black tracking-widest text-amber-800 mb-1">
                  Invite tes amis
                </div>
                <div className="text-sm text-slate-700 mb-3">
                  Partage ce code pour qu'ils rejoignent <strong>{user.league_name}</strong> :
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white rounded-full px-4 py-3 border-2 border-[color:var(--sunshine)]/60 font-mono font-black text-2xl text-center tabular-nums tracking-[0.3em] text-[color:var(--deep)]" data-testid="league-invite-code">
                    {myLeague.code}
                  </div>
                  <Button
                    onClick={() => copyCode(myLeague.code)}
                    data-testid="copy-league-code"
                    className="rounded-full h-12 w-12 p-0 bg-[color:var(--deep)] hover:bg-[color:var(--deep)]/90 text-white flex-shrink-0"
                    title="Copier"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => {
                      const link = `${window.location.origin}/join/${myLeague.code}`;
                      const msg = `Rejoins-moi sur Ma Petite Yole ! Ligue "${user.league_name}" — clique : ${link}`;
                      if (navigator.share) {
                        navigator.share({ title: "Ma Petite Yole", text: msg, url: link }).catch(() => {});
                      } else {
                        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
                      }
                    }}
                    data-testid="share-league-code"
                    className="rounded-full h-12 w-12 p-0 bg-[color:var(--palm)] hover:bg-[color:var(--palm)]/90 text-[color:var(--deep)] flex-shrink-0"
                    title="Partager"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
                <button
                  onClick={() => copyInviteLink(myLeague.code)}
                  data-testid="copy-invite-link"
                  className="mt-3 w-full flex items-center justify-center gap-2 text-xs font-bold text-[color:var(--deep)] bg-white/70 hover:bg-white border border-[color:var(--sunshine)]/50 rounded-full h-9 transition"
                  title="Copier le lien magique d'invitation"
                >
                  <Link2 className="h-3.5 w-3.5" />
                  Copier le lien magique (/join/{myLeague.code})
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {user?.league_is_default && (
              <div
                data-testid="default-league-banner"
                className="mb-4 rounded-2xl bg-[color:var(--sunshine)]/15 border-2 border-dashed border-[color:var(--sunshine)] p-4"
              >
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1">
                  Ta ligue actuelle
                </div>
                <div className="font-display font-extrabold text-lg text-[color:var(--deep)]">
                  Les sans ligue
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Tu joues dans la ligue commune de tous les visiteurs solo. Tu apparais dans
                  le classement global et tu peux faire tes pronostics normalement.
                  Rejoins ou crée une vraie ligue ci-dessous pour jouer avec tes amis.
                </p>
              </div>
            )}
            <form onSubmit={joinLeague} className="space-y-2" data-testid="join-league-form">
              <Label className="text-xs font-bold uppercase text-slate-600">Rejoindre avec un code</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="join-league-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="ex. TOUR2026"
                  className="h-11 rounded-xl font-mono tracking-widest"
                />
                <Button
                  type="submit"
                  data-testid="join-league-btn"
                  disabled={joining || !code.trim()}
                  className="rounded-full h-11 bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold px-5"
                >
                  {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-3.5 w-3.5 mr-1" /> Rejoindre</>}
                </Button>
              </div>
            </form>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center"><span className="bg-white px-3 text-[10px] uppercase tracking-widest font-bold text-slate-400">ou crée la tienne</span></div>
            </div>

            <form onSubmit={createLeague} className="space-y-2" data-testid="create-league-form">
              <Label className="text-xs font-bold uppercase text-slate-600">Nouvelle ligue</Label>
              <Input
                data-testid="create-league-name"
                value={newLeagueName}
                onChange={(e) => setNewLeagueName(e.target.value)}
                placeholder="ex. Copains du bureau"
                className="h-11 rounded-xl"
                maxLength={60}
              />
              <div className="flex gap-2">
                <Input
                  data-testid="create-league-code"
                  value={newLeagueCode}
                  onChange={(e) => setNewLeagueCode(e.target.value.toUpperCase())}
                  placeholder="Code auto si vide"
                  className="h-11 rounded-xl font-mono tracking-widest"
                  maxLength={12}
                />
                <Button
                  type="submit"
                  data-testid="create-league-btn"
                  disabled={creating || !newLeagueName.trim()}
                  className="rounded-full h-11 bg-[color:var(--palm)] hover:bg-[color:var(--palm)]/90 text-[color:var(--deep)] font-bold px-5"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" /> Créer</>}
                </Button>
              </div>
              <p className="text-[11px] text-slate-400">Tu rejoins automatiquement la ligue que tu crées.</p>
            </form>

            {lastCreated && (
              <div className="mt-3 rounded-xl bg-[color:var(--palm)]/15 border border-[color:var(--palm)]/40 p-3 text-sm text-slate-700" data-testid="last-created-league">
                Ligue "<strong>{lastCreated.name}</strong>" créée — le code d'invitation est affiché ci-dessus.
              </div>
            )}
          </>
        )}
      </div>

      {/* Favorite yole */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
          <Star className="h-3.5 w-3.5 text-[color:var(--sunshine)]" /> Yole favorite <span className="text-[color:var(--palm)] normal-case">(+5 pts si elle gagne une étape)</span>
        </div>
        {yoles.length === 0 ? (
          <div className="text-sm text-slate-400 py-3">Aucune yole enregistrée pour l'instant.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {yoles.map((y) => {
              const active = selectedFav === y.id;
              return (
                <button
                  key={y.id}
                  onClick={() => saveFavorite(active ? "" : y.id)}
                  disabled={savingFav}
                  data-testid={`favorite-${y.id}`}
                  className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition text-left ${
                    active ? "border-[color:var(--sunshine)] bg-[color:var(--sunshine)]/10" : "border-slate-200 bg-white hover:border-slate-400"
                  }`}
                >
                  {y.photo_url ? (
                    <img src={photoUrl(y.photo_url)} alt={y.name} className="w-9 h-9 rounded-lg object-cover" style={{ backgroundColor: y.color }} />
                  ) : (
                    <div className="w-9 h-9 rounded-lg" style={{ backgroundColor: y.color }} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-xs truncate flex items-center gap-1">
                      {y.name}
                      {active && <Star className="h-3 w-3 fill-[color:var(--sunshine)] text-[color:var(--sunshine)]" />}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">{y.skipper || "—"}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
